import { Flight } from '@mui/icons-material';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { Box, Paper } from '@mui/material';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { MaritimeOrderInfo, OrderInfo } from 'adoms-common-lib';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { CustomerURLInformationUtil } from '../../common/customerURLInformationUtil';
import { OrderConfirmActionButtons } from '../../components/atoms/OrderConfirmActionButtons';
import { LoadingMark } from '../../components/atoms/LoadingMark';
import ApplicationTooBar from '../../components/molecule/ApplicationToolBar';
import InfoRow from '../../components/atoms/InfoRow';
import { MaritimeOrderDestinationInfo } from '../../components/molecule/MaritimeOrderDestination';
import MaritimeOrderGoods from '../../components/molecule/MaritimeOrderGoods';
import { MaritimeOrderRequesterInfo } from '../../components/molecule/MaritimeOrderRequesterInfo';
import OrderStepBar, { Process } from '../../components/molecule/OrderStepBar';
import { APIConnector } from '../../connector/APIConnector';
import { OrderInfoNotFound } from '../../components/organisms/OrderInfoNotFound';
import { SectionGrid } from '../../components/organisms/SectionGrid';

/**
 * 配送依頼を確定する
 */
export default function MaritimeOrderConfirm() {
    const navigate = useNavigate();
    const location = useLocation();

    const state = (location.state as {
        order?: MaritimeOrderInfo;
        qrCodeErrorList?: string[];
        displayQrCodeScanResult?: string[];
        imageBase64?: string;
        imageUrl?: string;
    } | null | undefined);

    // 脆弱性対策のためシャローコピー（null/undefined を spread すると落ちるのでガード）
    const [customerURLInformation] = React.useState(() => {
        const info = CustomerURLInformationUtil.instance.getCustomerURLInformation();
        return info ? { ...info } : undefined;
    });

    const order = state?.order;
    const qrCodeErrorList = state?.qrCodeErrorList;
    const displayQrCodeScanResult = state?.displayQrCodeScanResult;
    const imageBase64 = state?.imageBase64;
    const imageUrlFromState = state?.imageUrl;
    //次へボタンをdiableにするかどうかのフラグ
    const [isDisableFlag, setDisableFlag] = React.useState(false);
    const [isDisplayLoadingMark, setDisplayLoadingMark] = React.useState(false);
    const submitLockRef = React.useRef(false);
    //エラーメッセージを表示するかどうかのフラグ
    const [isErrorFlag, setErrorFlag] = React.useState(false);
    const displayValue = (value: string | undefined) => (value && value !== '' ? value : 'ー');

    useEffect(() => {
        if (!customerURLInformation) {
            navigate("/forbidden403Page");
        }
    }, [customerURLInformation, imageBase64, imageUrlFromState, navigate]);

    /**
     * 次のページに遷移する際の処理（オーダー処理）
     * @param event 
     */
    const handleToNextPage = async (event: any, order: OrderInfo) => {
        if (submitLockRef.current) return;
        submitLockRef.current = true;
        setDisableFlag(true);
        setDisplayLoadingMark(true);
        setErrorFlag(false);

        try {
            const tenantId = customerURLInformation?.tenantId;
            console.log("customerURLInformation.urlId:" + customerURLInformation?.urlId);
            if (tenantId === undefined) {
                setErrorFlag(true);
                return;
            }

            //orderをディープコピーしてからweightを修正
            const orderCopy = structuredClone(order);
            const apigw: APIConnector = APIConnector.instance;
            const value = await apigw.putOrder(orderCopy, tenantId);
            setDisableFlag(false);
            setDisplayLoadingMark(false);
            navigate(
                "/ordercomplete/" + customerURLInformation?.urlId,
                {
                    state: { order: value as OrderInfo },
                })
        } catch (error) {
            setErrorFlag(true);
            console.log("エラー:" + error);
        } finally {
            submitLockRef.current = false;
            setDisableFlag(false);
            setDisplayLoadingMark(false);
        }
    };

    /**
     * OrderFlightSearchInputページに遷移する際の処理
     * @param event 
     */
    const handleToOrderFlightSearchInputPage = (event: any, displayQrCodeScanResult: string[] | undefined, order?: OrderInfo, qrCodeErrorList?: string[]) => {
        if (!customerURLInformation?.urlId) {
            navigate('/forbidden403Page');
            return;
        }
        navigate(
            "/orderflightsearchinput/" + customerURLInformation?.urlId,
            {
                state: {
                    orderForEdit: order,
                    qrCodeErrorList: qrCodeErrorList,
                    displayQrCodeScanResult: displayQrCodeScanResult,
                }
            });
    };

    const handleNavigateTopPage = async () => {
        const urlId = customerURLInformation?.urlId;
        if (!urlId) {
            navigate('/forbidden403Page');
            return;
        }
        await CustomerURLInformationUtil.instance.updateCustomerURLInformationByUrlId(urlId);
        navigate(`/${urlId}`);
    };

    if (!order) {
        return (
            <OrderInfoNotFound
                appBarLabel="配送依頼"
                process={Process.Confirm}
                onNavigateHome={handleNavigateTopPage}
            />
        );
    }

    return (
        <Grid container justifyContent="center" xs={12} spacing={1} mb={1}>
            <Grid item xs={12} sm={12} >
                <ApplicationTooBar label="配送依頼" />
            </Grid>
            <Grid item xs={12} sm={12}>
                <OrderStepBar process={Process.Confirm} />
            </Grid>
            <Grid item xs={11} sm={11} mb={3}>
                {isErrorFlag ? (
                    <Alert severity="error" >配送依頼中にエラーが発生しました。システム管理者へご連絡ください</Alert>
                ) : (
                    <Alert severity="info" sx={{ border: '1px solid #e0e0e0', backgroundColor: "#e3f2fd" }}>以下の内容で配送依頼します</Alert>
                )}
            </Grid>
            <SectionGrid title="フライト情報" icon={<Flight sx={{ rotate: "90deg" }} />}>
                <Paper sx={{
                    textAlign: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%'
                }} elevation={0}>
                    {/* 日付ヘッダー */}
                    <Typography sx={{ display: 'block', textAlign: 'center', mb: 1 }}>
                        {displayValue(order.preferredDeliveryDate)}
                    </Typography>
                    <Grid container alignItems="center" justifyContent="center">
                        {/* 出発地と出発時間 */}
                        <Grid item xs={5} textAlign="center">
                            <Box>
                                <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>出発地</Typography>
                                <Typography variant="body1" sx={{ color: 'grey.800', mb: 0.5, fontWeight: 'bold' }}>{displayValue(order.departure.name)}</Typography>
                                <Typography>
                                    {displayValue(order.preferredDeliveryTime.split('-')[0])}
                                </Typography>
                            </Box>
                        </Grid>
                        {/* 矢印アイコン */}
                        <Grid item xs={2} textAlign="center">
                            <Flight sx={{ color: 'grey.500', fontSize: 24, rotate: '90deg' }} />
                        </Grid>
                        {/* 到着地と到着時間 */}
                        <Grid item xs={5} textAlign="center">
                            <Box>
                                <Typography variant="caption" sx={{ color: 'grey.500', display: 'block' }}>到着地</Typography>
                                <Typography variant="body1" sx={{ color: 'grey.800', mb: 0.5, fontWeight: 'bold' }}>{displayValue(order.arrival.name)}</Typography>
                                <Typography>
                                    {displayValue(order.preferredDeliveryTime.split('-')[1])}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            </SectionGrid>
            <SectionGrid title="配送情報" icon={<Inventory2OutlinedIcon />}>
                <InfoRow label="配送ルート" value={order.transportRoute} />
                <InfoRow label="補給本部伝票番号" value={order.supplyHqVoucherNumber} />
                {order.carrierWaybillNumber && (
                    <InfoRow label="日通運送状番号" value={order.carrierWaybillNumber} />
                )}
            </SectionGrid>
            <SectionGrid title="配送物" icon={<Inventory2OutlinedIcon />}>
                <MaritimeOrderGoods order={order} />
            </SectionGrid>
            <SectionGrid title="荷送人様の情報" icon={<LocalShippingOutlinedIcon />}>
                <MaritimeOrderRequesterInfo order={order} />
            </SectionGrid>
            <SectionGrid title="荷受人様の情報" icon={<DirectionsBoatIcon />}>
                <MaritimeOrderDestinationInfo order={order} />
            </SectionGrid>
            <OrderConfirmActionButtons
                onCancel={(e) => handleToOrderFlightSearchInputPage(e, undefined)}
                onEdit={(e) => handleToOrderFlightSearchInputPage(e, displayQrCodeScanResult, order, qrCodeErrorList)}
                onConfirm={(e) => handleToNextPage(e, order)}
                disabled={isDisableFlag}
                loading={isDisplayLoadingMark}
            />
            <LoadingMark isDisplayLoadingMark={isDisplayLoadingMark} />
        </Grid>
    );
}